/**
 * Client funnel telemetry (P1 infra) — the launch was flying blind. A tiny, self-hosted, NO-WIRE beacon: queue a
 * handful of whitelisted funnel events and POST them as one small JSON batch to the game server's `/telemetry`
 * HTTP endpoint via `navigator.sendBeacon` (falling back to `fetch(..., {keepalive})`), flushing on
 * visibilitychange / pagehide so a backgrounded mobile tab still delivers.
 *
 * PRIVACY: NO PII leaves the browser. The only identifier is `anonId()` — a short, NON-reversible hash of the
 * device token (never the raw token, never the captain name). Everything is fire-and-forget; a failed/blocked
 * beacon is silently dropped (analytics must never affect gameplay).
 *
 * The core (`anonId`, `TelemetryClient`) is pure + dependency-injected so it unit-tests with no DOM/network; the
 * browser singleton at the bottom wires the real sendBeacon + lifecycle listeners.
 */
import { resolveServerUrl } from './config.js';

/** The STRICT whitelist — MUST mirror the server's TELEMETRY_TYPES (apps/server/src/telemetry.ts). */
export const TELEMETRY_TYPES = [
  'client_boot', 'menu_shown', 'first_spawn', 'ftue_step', 'ftue_done', 'first_kill', 'session_end', 'push_optin',
  // ftue_step/ftue_done above are LEGACY (the quest-FTUE was removed 2026-07-16) — kept in the whitelist so the
  // shared type + the server mirror stay tolerant of old clients still in the wild; this client no longer emits them.
  'primer_seen',      // the Captain's Primer manual was opened for the first time on this device
  'primer_dismissed', // the manual was closed — `k` = deepest card reached (p1..pN), `d` = seconds it was open
  'primer_reopened',  // the manual was opened again (a later open on the same device)
  'tut_step',         // Helm Coach: entered a step — `k` = the step key (steer/sails/aim/bail/wind/chart)
  'tut_skip',         // Helm Coach: skipped — `k` = the step it died on
  'tut_done',         // Helm Coach: the final step completed
  // First-60-seconds-at-sea breadcrumbs (Onda First-60s, front C) — the earliest engagement signals, fired at
  // most once per session. They read whether a fresh captain actually TOUCHED the game before bouncing (median
  // session is only ~14 s), independent of the Helm Coach (which veterans/skippers never see).
  'sea_control',      // first REAL control at sea — `k` = 'helm' (took the wheel), 'sail' (trimmed the sheets), or
                      // 'chart' (laid a tap-to-sail course — the quartermaster then steers, so the raw thumb never
                      // moves; without this the coach-taught tap-to-sail captain read as "never took control")
  'sea_shot',         // first broadside fired
  'sea_60s',          // survived 60 s of ACTIVE sea time (alive, at sea) — cleared the bounce zone
  'client_error', // window.onerror / unhandledrejection beacon (truncated message + stack + source url)
  'webgl_unsupported', // dedicated dead-screen signal: WebGL2 context creation failed → the diegetic fallback page was shown
  'connect', // handshake reliability funnel — the `k` (kind) carries the mode: start/ok/timeout/welcome_timeout/retry/version/failed/reject (see net.ts). Isolates connection loss from download/abandon inside the boot→spawn gap.
  'funnel',  // boot→spawn breadcrumbs — the `k` (stage) carries play/socket_open/welcome/world/first_frame (see telemetryFunnel).
             // `d` = ms elapsed since the SET SAIL tap. Splits the blind connect→first_spawn stretch so we can tell whether
             // a lost captain died at the socket, the handshake, the world sync, the first render, or the menu decision.
  'ext_noise', // a 3rd-party browser-extension error (MetaMask/Keplr/web3 injecting into our window) — recorded for
               // visibility but DELIBERATELY kept OUT of the client_error funnel metric so it never masks a real bug
] as const;
export type TelemetryType = (typeof TELEMETRY_TYPES)[number];

export interface QueueEvent { t: TelemetryType; k?: string; d?: number; m?: string; st?: string; u?: string; }

/** Truncation budgets for a client_error payload (mirror the server's caps in telemetry.ts). */
const ERR_MSG_MAX = 300;
const ERR_STACK_MAX = 1024;
const ERR_URL_MAX = 200;
const ERR_PER_SESSION = 10; // never beacon more than this many distinct errors per session (bounded spam)
const NOISE_PER_SESSION = 5; // ext_noise gets its OWN small budget so wallet-extension spam can't crowd out real errors

/* ── 3rd-party noise classification (pure) ──────────────────────────────────────────────────────────────────
 * Crypto-wallet browser extensions (MetaMask, Keplr, Phantom, Coinbase, …) inject a provider into every page's
 * `window` and routinely throw while probing/redefining `window.ethereum|solana` — on a page that has nothing to
 * do with them. Those uncaught errors were landing in our `client_error` funnel metric and MASKING real boot bugs.
 * We still record them (as `ext_noise`) for visibility, but tag them so the summary never counts them as OUR errors. */
const EXT_SCHEME_RE = /(?:chrome-extension|moz-extension|safari-web-extension|safari-extension|ms-browser-extension|webkit-masked-url):\/\//i;
/** Known wallet-injection failure text (matched against the MESSAGE only, so a stack token never over-tags). */
const WALLET_MSG_RE = /\b(?:metamask|keplr|phantom|coinbase\s*wallet|walletconnect|evmask|trust\s*wallet)\b|(?:redefine|set|assign to read[- ]only|define) property[ :'"]*ethereum|reading ['"]?ethereum|cannot read propert(?:y|ies) of (?:undefined|null) \(reading ['"]?ethereum|window\.(?:ethereum|solana)|\bethereum#|isMetaMask|selectedAddress/i;

/** PURE: is this uncaught error 3rd-party browser-extension noise (a wallet provider), NOT our code? */
export function isExtensionError(message = '', stack = '', url = ''): boolean {
  if (EXT_SCHEME_RE.test(`${stack}\n${url}`)) return true; // a frame/source that lives inside an extension
  if (WALLET_MSG_RE.test(String(message))) return true;    // a recognised wallet-injection failure message
  return false;
}

/** PURE: a benign ServiceWorker-registration abort (the tab was closed before the SW finished registering). Not
 *  an error at all — a captain leaving mid-boot. Dropped entirely so it never appears in the funnel. */
export function isBenignAbort(message = '', stack = ''): boolean {
  const hay = `${message}\n${stack}`;
  const aborted = /abort(?:ed|error)|operation (?:has been|was) aborted/i.test(hay);
  return aborted && /serviceworker|register a serviceworker|sw\.js/i.test(hay);
}

/** Tiny FNV-1a hash → hex, used to dedupe repeated error messages within a session. */
function hashMsg(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0;
  return (h >>> 0).toString(16);
}

/**
 * Short, NON-reversible id derived from the device token — pseudonymous, so the funnel can count unique captains
 * without ever seeing the raw token or any real-world identity. Two independent 32-bit hashes (different seeds)
 * → 12 lowercase hex chars (~48 bits). Empty token → '' (anonymous). Pure.
 */
export function anonId(token: string): string {
  if (!token) return '';
  let h1 = 0x811c9dc5, h2 = 0xc2b2ae35;
  for (let i = 0; i < token.length; i++) {
    const c = token.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x85 ^ 0x27d4eb2f) >>> 0;
  }
  const hex = (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
  return hex.slice(0, 12);
}

export interface TelemetryDeps {
  /** Hand a batch body off to the network. Returns true if accepted (queued by the UA). Injected for tests. */
  send: (url: string, body: string) => boolean;
  /** HTTP base of the game server (e.g. https://host); '' disables sending. */
  httpBase: () => string;
  /** The anon id for this browser (see anonId). */
  anonId: () => string;
  /** ms-epoch clock (injectable). */
  now: () => number;
  /** A random per-session id (opaque). */
  sessionId: string;
  /** Flush automatically once the queue reaches this many events (default 12). */
  maxQueue?: number;
}

export class TelemetryClient {
  private queue: QueueEvent[] = [];
  private fired = new Set<string>();      // once-per-session dedupe keys
  private errSeen = new Set<string>();    // client_error message hashes already reported this session
  private errCount = 0;                   // client_error budget guard (ERR_PER_SESSION)
  private noiseSeen = new Set<string>();  // ext_noise message hashes already reported this session
  private noiseCount = 0;                 // ext_noise budget guard (NOISE_PER_SESSION)
  private sessionStart: number;
  private ended = false;
  // How THIS session began, tagged onto session_end so the summary can read an honest median over genuine new
  // opens ('fresh') instead of one inflated by tab-refocus ('resume') and mid-session socket-drop ('reconnect')
  // blips. Starts 'fresh'; resumeSession() flips it to 'resume'; markReconnect() upgrades a still-'fresh' session
  // to 'reconnect' (a resumed session stays 'resume' — the dominant blip cause).
  private sessionKind: 'fresh' | 'resume' | 'reconnect' = 'fresh';
  private readonly maxQueue: number;

  constructor(private readonly d: TelemetryDeps) {
    this.sessionStart = d.now();
    this.maxQueue = d.maxQueue ?? 12;
  }

  /** Enqueue an event. `once` (a dedupe key) fires it at most once per session. Auto-flushes when the queue fills. */
  track(t: TelemetryType, opts: { k?: string; d?: number; once?: string; m?: string; st?: string; u?: string } = {}): void {
    if (opts.once) { if (this.fired.has(opts.once)) return; this.fired.add(opts.once); }
    const e: QueueEvent = { t };
    if (opts.k) e.k = opts.k;
    if (typeof opts.d === 'number' && Number.isFinite(opts.d)) e.d = Math.max(0, Math.floor(opts.d));
    if (opts.m) e.m = opts.m;
    if (opts.st) e.st = opts.st;
    if (opts.u) e.u = opts.u;
    this.queue.push(e);
    if (this.queue.length >= this.maxQueue) this.flush();
  }

  /**
   * Report an uncaught client error (window.onerror / unhandledrejection). Truncates the message + stack + source,
   * dedupes by message hash so the same error is beaconed once per session, and caps the total per session. Flushes
   * immediately so a fatal boot crash still delivers before the tab dies. Never throws.
   */
  trackError(message: string, stack = '', url = ''): void {
    const msg = String(message || 'error').slice(0, ERR_MSG_MAX);
    const key = hashMsg(msg);
    if (this.errSeen.has(key)) return;              // same message already reported this session
    if (this.errCount >= ERR_PER_SESSION) return;   // session budget spent
    this.errSeen.add(key);
    this.errCount++;
    this.track('client_error', { m: msg, st: String(stack || '').slice(0, ERR_STACK_MAX), u: String(url || '').slice(0, ERR_URL_MAX) });
    this.flush(); // errors are rare + we want boot crashes delivered promptly, not held until pagehide
  }

  /**
   * Report a 3rd-party browser-extension error (a wallet provider injecting into our window). Same truncation +
   * dedupe as trackError but on its OWN small budget and under the `ext_noise` type — so it is visible in the raw
   * telemetry yet NEVER counted in the client_error funnel metric. Never throws.
   */
  trackNoise(message: string, stack = '', url = ''): void {
    const msg = String(message || 'ext').slice(0, ERR_MSG_MAX);
    const key = hashMsg(msg);
    if (this.noiseSeen.has(key)) return;             // same noise already reported this session
    if (this.noiseCount >= NOISE_PER_SESSION) return; // session budget spent
    this.noiseSeen.add(key);
    this.noiseCount++;
    this.track('ext_noise', { m: msg, st: String(stack || '').slice(0, ERR_STACK_MAX), u: String(url || '').slice(0, ERR_URL_MAX) });
    this.flush();
  }

  /** POST whatever is queued as one batch (fire-and-forget). Clears the queue on a successful handoff. */
  flush(): void {
    if (this.queue.length === 0) return;
    const base = this.d.httpBase();
    if (!base) { this.queue = []; return; } // no server URL — drop silently
    const body = JSON.stringify({ id: this.d.anonId(), sid: this.d.sessionId, v: 1, events: this.queue });
    let ok = false;
    try { ok = this.d.send(`${base}/telemetry`, body); } catch { ok = false; }
    // Whether or not the UA accepted it, clear the queue — telemetry is best-effort and must never grow unbounded.
    this.queue = [];
    void ok;
  }

  /** End the session: emit session_end with its duration (seconds) + its kind, then flush. Idempotent per session. */
  endSession(): void {
    if (this.ended) { this.flush(); return; }
    this.ended = true;
    const dur = Math.max(0, Math.round((this.d.now() - this.sessionStart) / 1000));
    this.track('session_end', { d: dur, k: this.sessionKind, once: 'session_end' });
    this.flush();
  }

  /** A backgrounded tab that came back to life — count it as a fresh (but tagged 'resume') session so durations
   *  stay honest and the summary can exclude these blips from the engaged median. */
  resumeSession(newSessionId?: string): void {
    if (!this.ended) return;
    this.ended = false;
    this.sessionStart = this.d.now();
    this.sessionKind = 'resume';
    this.fired.delete('session_end');
    if (newSessionId) (this.d as { sessionId: string }).sessionId = newSessionId;
  }

  /** The live socket dropped and net auto-reconnected mid-session. Tags a still-'fresh' session as 'reconnect'
   *  so a network-churn blip doesn't masquerade as a clean new session in the median. No-op once resumed/ended. */
  markReconnect(): void {
    if (this.ended) return;
    if (this.sessionKind === 'fresh') this.sessionKind = 'reconnect';
  }

  /** Test/debug introspection — no side effects. */
  pending(): number { return this.queue.length; }
}

/* ============================================ browser singleton ============================================ */

const TOKEN_KEY = 'soh_device_token'; // the SAME device token net.ts/recovery.ts/pushclient.ts use (→ anon id)

function readToken(): string { try { return localStorage.getItem(TOKEN_KEY) ?? ''; } catch { return ''; } }
function httpBase(): string {
  try { return resolveServerUrl().replace(/^ws/, 'http').replace(/\/+$/, ''); } catch { return ''; }
}
function randomSid(): string {
  try {
    const b = new Uint8Array(8); (crypto as Crypto).getRandomValues(b);
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  } catch { return Math.random().toString(16).slice(2, 18); }
}
/** Real network handoff: sendBeacon (survives page unload) with a keepalive fetch fallback. */
function browserSend(url: string, body: string): boolean {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      return navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    }
  } catch { /* fall through */ }
  try { void fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body, keepalive: true }).catch(() => {}); return true; } catch { return false; }
}

let singleton: TelemetryClient | null = null;
/** Set once the boot has been deliberately halted (e.g. WebGL2 unavailable → fallback page shown). After this,
 *  the global error handlers stop beaconing — the halt is INTENTIONAL, so the cascade of downstream errors it
 *  triggers is noise, and the real cause already rode its own dedicated event. */
let bootHalted = false;

/** Boot the telemetry singleton + lifecycle flush listeners. Safe to call once at startup; idempotent. */
export function initTelemetry(): TelemetryClient {
  if (singleton) return singleton;
  let sid = randomSid();
  singleton = new TelemetryClient({
    send: browserSend, httpBase, now: () => Date.now(), sessionId: sid,
    anonId: () => anonId(readToken()),
  });
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (!singleton) return;
      if (document.visibilityState === 'hidden') singleton.endSession();
      else { sid = randomSid(); singleton.resumeSession(sid); }
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => singleton?.endSession());
    // Error tracking: capture uncaught errors + unhandled promise rejections as `client_error` beacons. Installed
    // here (initTelemetry runs early in main.ts boot) so a crash while the game is initialising is still reported.
    window.addEventListener('error', (ev: ErrorEvent) => {
      if (bootHalted) return; // boot was deliberately halted (WebGL fallback) — downstream errors are noise
      try {
        const err = ev.error as { message?: string; stack?: string } | undefined;
        const msg = String(ev.message || err?.message || 'error');
        const stack = err?.stack ? String(err.stack) : '';
        const src = `${ev.filename || ''}:${ev.lineno || 0}:${ev.colno || 0}`;
        // Route away from the funnel: a captain who closed the tab mid-boot (SW-register abort) is not an error;
        // a wallet extension throwing into our window is noise, not OUR bug. Neither may pollute client_error.
        if (isBenignAbort(msg, stack)) return;
        if (isExtensionError(msg, stack, ev.filename || '')) { singleton?.trackNoise(msg, stack, src); return; }
        singleton?.trackError(msg, stack, src);
      } catch { /* the error handler must never throw */ }
    });
    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      if (bootHalted) return; // see the 'error' handler above
      try {
        const r = ev.reason as { message?: string; stack?: string } | undefined;
        const msg = String(r?.message ? r.message : String(r ?? 'unhandledrejection'));
        const stack = r?.stack ? String(r.stack) : '';
        if (isBenignAbort(msg, stack)) return; // a ServiceWorker-register abort on tab-close is not an error
        if (isExtensionError(msg, stack)) { singleton?.trackNoise(msg, stack, 'unhandledrejection'); return; }
        singleton?.trackError(msg, stack, 'unhandledrejection');
      } catch { /* swallow */ }
    });
  }
  return singleton;
}

/** Convenience: enqueue an event on the singleton (no-op before initTelemetry). */
export function track(t: TelemetryType, opts: { k?: string; d?: number; once?: string } = {}): void {
  singleton?.track(t, opts);
}

/**
 * The dead-screen signal. Fired ONCE when WebGL2 context creation fails and the diegetic fallback page is shown
 * instead of a silent black canvas. Carries a truncated reason (as a client_error-style `m`) so we can tell a
 * blacklisted GPU from a missing-WebGL2 browser. Also flips `bootHalted` so the sentinel rethrow that stops the
 * boot doesn't get double-beaconed as a generic client_error. Flushes immediately (the tab may never navigate).
 */
export function telemetryWebglUnsupported(reason = '', webview = false): void {
  bootHalted = true;
  if (!singleton) return;
  // `k` splits the dead-screen by context: a WebGL2 failure inside an in-app webview (social traffic that can be
  // SAVED with "open in your browser") vs. a genuine no-WebGL2 browser/GPU. The aggregator reads webgl_unsupported
  // `k` to size the recoverable slice.
  singleton.track('webgl_unsupported', { once: 'webgl_unsupported', k: webview ? 'webview' : 'browser', m: String(reason || 'no-webgl2').slice(0, 200) });
  singleton.flush();
}

// Named funnel helpers — each fires at most once per session unless noted.
export const telemetryBoot = (): void => track('client_boot', { once: 'client_boot' });
export const telemetryMenuShown = (): void => track('menu_shown', { once: 'menu_shown' });
export const telemetryFirstSpawn = (): void => track('first_spawn', { once: 'first_spawn' });
export const telemetryFirstKill = (): void => track('first_kill', { once: 'first_kill' });
export const telemetryPushOptin = (): void => track('push_optin', { once: 'push_optin' });
// Captain's Primer (the FTUE's replacement — ftue_step/ftue_done are no longer emitted, see the whitelist note).
// `seen` is once per session (it's also once per device by construction); a captain may legitimately re-open and
// re-dismiss several times, so those two are NOT deduped — the server aggregates uniques per device anyway.
export const telemetryPrimerSeen = (): void => track('primer_seen', { once: 'primer_seen' });
export const telemetryPrimerReopened = (): void => track('primer_reopened', {});
export const telemetryPrimerDismissed = (k: string, d: number): void => track('primer_dismissed', { k, d });
// Helm Coach (contextual HUD tutorial): step entry is once per key per session; skip/done once per session.
export const telemetryTutStep = (key: string): void => track('tut_step', { k: key, once: `tut_step:${key}` });
export const telemetryTutSkip = (key: string): void => track('tut_skip', { k: key, once: 'tut_skip' });
export const telemetryTutDone = (): void => track('tut_done', { once: 'tut_done' });

// First-60-seconds-at-sea breadcrumbs (front C) — each at most once per session. These fire for EVERY captain
// (coach or not), so tomorrow's read can see how many fresh spawns actually took the wheel / fired / lasted a
// minute before the ~14 s median bounce.
export const telemetrySeaControl = (k: 'helm' | 'sail' | 'chart'): void => track('sea_control', { k, once: `sea_control:${k}` });
export const telemetrySeaShot = (): void => track('sea_shot', { once: 'sea_shot' });
export const telemetrySea60 = (): void => track('sea_60s', { once: 'sea_60s' });

/** net.ts calls this when the live socket dropped and it auto-reconnected mid-session, so the session's
 *  session_end is tagged 'reconnect' (a network-churn blip) instead of counting as a clean session. */
export const telemetryMarkReconnect = (): void => singleton?.markReconnect();

/** Connect-reliability funnel signal (net.ts owns the vocabulary). NOT once-per-session: a flaky handshake
 *  legitimately emits `retry`/`timeout` several times before an `ok`/`failed`. The server aggregates unique
 *  captains per kind, so repeats from one device still collapse to one in the funnel denominators. */
export const telemetryConnect = (kind: 'start' | 'ok' | 'timeout' | 'welcome_timeout' | 'retry' | 'version' | 'failed' | 'reject'): void => track('connect', { k: kind });

/* ── boot→spawn funnel breadcrumbs (the blind connect→first_spawn stretch) ──────────────────────────────────
 * Ordered stages between "SET SAIL tapped" and "reached open water" (first_spawn), each fired at most once per
 * session. `play` anchors t0; every later stage carries `d` = ms since that tap so tomorrow's summary can read
 * both HOW MANY captains reach each stage (the drop location) and HOW LONG it took (the slow-loading signal):
 *   play        — SET SAIL tapped / auto-start (main.ts startGame). No `d` (it IS t0).
 *   socket_open — the WebSocket opened (net.ts onopen). d = ms to open the socket.
 *   welcome     — the first S_WELCOME / S_PORTWELCOME landed (net.ts). d = ms to a completed handshake.
 *   world       — S_WORLD (the static sea layout) arrived (net.ts). NB a house-born newcomer receives this only
 *                 once they reach the sea, so for them `world` trails `first_frame` (the house renders first).
 *   first_frame — the first scene was actually drawn (main.ts loop). d = ms to first paint of a playable scene.
 * The existing `first_spawn` event closes the funnel (controllable at open sea). NO WIRE, NO PII. */
export type FunnelStage = 'play' | 'socket_open' | 'welcome' | 'world' | 'first_frame';
let funnelT0 = 0;
const funnelClock = (): number => (typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now());
export function telemetryFunnel(stage: FunnelStage): void {
  const now = funnelClock();
  if (stage === 'play') { funnelT0 = now; track('funnel', { k: stage, once: `funnel:${stage}` }); return; }
  const d = funnelT0 > 0 ? Math.max(0, Math.round(now - funnelT0)) : undefined;
  track('funnel', { k: stage, d, once: `funnel:${stage}` });
}
